import { useCallback, useEffect, useRef, useState } from "react";
import { ethers } from "ethers";
import type { TokenInfo } from "../../config";
import { withDeadline } from "../../utils/withDeadline";

export async function readWalletTokenBalance(
  signer: ethers.Signer,
  token: TokenInfo,
): Promise<bigint> {
  return withDeadline(async () => {
    const address = await signer.getAddress(),
      provider = signer.provider;
    if (!provider) throw Error("Connect your wallet to check your balance.");
    if (token.isNative) return provider.getBalance(address);
    if (token.tokenType === "erc721") {
      const owner = await new ethers.Contract(
        token.address,
        ["function ownerOf(uint256) view returns(address)"],
        provider,
      ).ownerOf(BigInt(token.tokenSubID || "0"));
      return owner.toLowerCase() === address.toLowerCase() ? 1n : 0n;
    }
    if (token.tokenType === "erc1155")
      return new ethers.Contract(
        token.address,
        ["function balanceOf(address,uint256) view returns(uint256)"],
        provider,
      ).balanceOf(address, BigInt(token.tokenSubID || "0"));
    return new ethers.Contract(
      token.address,
      ["function balanceOf(address) view returns(uint256)"],
      provider,
    ).balanceOf(address);
  }, "Unable to check your balance. Refresh and try again.");
}

export function useWalletTokenBalance(
  account: string | null | undefined,
  chainId: number,
  signer: ethers.Signer | null,
  token: TokenInfo | null | undefined,
) {
  const tokenKey = (t: TokenInfo) =>
    `${t.address.toLowerCase()}:${t.tokenType}:${t.tokenSubID || ""}:${t.decimals}`;
  const key =
    account && signer && token
      ? `${account.toLowerCase()}:${chainId}:${tokenKey(token)}`
      : "";
  const currentKey = useRef(key);
  currentKey.current = key;
  const generation = useRef(0);
  const [loaded, setLoaded] = useState<{
    key: string;
    balance: string | null;
  } | null>(null);
  const refresh = useCallback(
    async (selected: TokenInfo) => {
      if (!key || !signer || !key.endsWith(`:${tokenKey(selected)}`)) return;
      const request = ++generation.current;
      try {
        const balance = await readWalletTokenBalance(signer, selected);
        if (request === generation.current && currentKey.current === key)
          setLoaded({
            key,
            balance: ethers.formatUnits(balance, selected.decimals),
          });
      } catch {
        if (request === generation.current && currentKey.current === key)
          setLoaded({ key, balance: null });
      }
    },
    [key, signer],
  );
  useEffect(() => {
    setLoaded(null);
    if (token) void refresh(token);
    return () => {
      generation.current++;
    };
  }, [key, refresh, token]);
  return { balance: loaded?.key === key ? loaded.balance : null, refresh };
}
